/**
 * Heidi Work / Projects analytics helpers.
 *
 * Lives under models/analytics (not features/projects) so Chat-quarantined
 * surfaces can emit without growing chat→features outbound couplings.
 *
 * Consolidated behavioural events (oscerai/snowplow#154) — variation via
 * action / outcome / object_type props. Snowplow-first: amplitude/posthog off.
 * `client_platform` is auto-enriched by `lib/analytics/wiring.ts`.
 */
import {
  WorkLaunchEventName,
  type WorkLaunchEventName as WorkLaunchEventNameType,
} from '@/models/analytics/features/workLaunchEvents';
import type {
  WorkLaunchAnalyticsProps as WorkBaseProps,
  WorkExperienceFeedbackActionedProps,
  WorkSeatType,
  WorkUpgradeActionedProps,
} from '@heidi/agents/contracts';
import { getCurrentUserBillingInfo } from '@heidi/commerce/data';
import { track as trackViaPipeline } from '@heidi/runtime/analytics';
import type { BillingSeatType } from '@heidi/schema/ml-scribe';

export { WorkLaunchEventName };
export type { WorkLaunchEventNameType as WorkTrackableEvent };

// AGENTS-OWNED, re-exported name-intact. The surface union is part of the
// Work-home composer payload contract, which `@heidi/agents/contracts` now
// declares so the composer chrome can build that payload without reaching `@/`.
// Every importer still names this module.
export type { WorkSurface } from '@heidi/agents/contracts';

// Likewise the launch-event payload shapes (D9 ruling 27): the event NAMES are
// declared in `@heidi/agents/contributions` and their PROP TYPES in
// `@heidi/agents/contracts`, so the pair has one home. `WorkBaseProps` keeps
// its name at this path — every importer, and every `jest.mock` literal, still
// resolves unchanged.
export type { WorkLaunchAnalyticsProps } from '@heidi/agents/contracts';
export type { WorkBaseProps, WorkUpgradeActionedProps };

const DEFAULT_WORK_OPTIONS = {
  amplitude: false,
  segment: false,
  posthog: false,
} as const;

/** Fired-once guard for work_error_encountered across transport + status effect. */
const firedWorkErrors = new Set<string>();

export function trackWorkEvent(
  eventName: WorkLaunchEventNameType,
  props: WorkBaseProps = {},
  options?: { amplitude?: boolean; segment?: boolean; posthog?: boolean }
): void {
  trackViaPipeline({
    event_name: eventName,
    props,
    options: { ...DEFAULT_WORK_OPTIONS, ...options },
  });
}

/**
 * Emit work_error_encountered at most once per (project, run, error_type) key.
 */
export function trackProjectChatErrorOnce(
  props: WorkBaseProps & { error_type: string }
): void {
  const key = `${props.project_id ?? ''}:${props.agent_run_id ?? ''}:${props.error_type}`;
  if (firedWorkErrors.has(key)) return;
  firedWorkErrors.add(key);
  trackWorkEvent(WorkLaunchEventName.WorkErrorEncountered, {
    action: 'encountered',
    outcome: 'failed',
    object_type: 'turn',
    ...props,
  });
}

/** Test helper — resets the fired-once set. */
export function _resetProjectChatErrorGuardForTests(): void {
  firedWorkErrors.clear();
}

export function trackSidebarProjectSelected(props: WorkBaseProps = {}): void {
  trackWorkEvent(WorkLaunchEventName.WorkSurfaceActioned, {
    action: 'reopened',
    surface: 'sidebar',
    ...props,
  });
}

export function trackSidebarNewChatClicked(
  click_source: string,
  props: WorkBaseProps = {}
): void {
  trackWorkEvent(WorkLaunchEventName.WorkSurfaceActioned, {
    action: 'cta_clicked',
    surface: 'sidebar',
    entry_point: click_source,
    ...props,
  });
}

/**
 * The "Continue on mobile" card in Work chat (AGE-2889). Rides
 * work_surface_actioned 1-1-2: every prop and action value is already in the
 * schema, so no Snowplow change is owed.
 */
export function trackMobileHandoffCardActioned(
  action: 'viewed' | 'cta_clicked' | 'dismissed',
  props: WorkBaseProps = {}
): void {
  trackWorkEvent(WorkLaunchEventName.WorkSurfaceActioned, {
    action,
    surface: 'project_chat',
    entry_point: 'mobile_handoff_card',
    ...props,
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Experience Feedback (UMUX-Lite) — single event, action discriminator
// Schema: iglu:com.heidi/work_experience_feedback_actioned/jsonschema/1-0-2
// ─────────────────────────────────────────────────────────────────────────────

// Payload contract, so it lives with the others in `@heidi/agents/contracts`
// and keeps its name here.
export type { WorkExperienceFeedbackAction } from '@heidi/agents/contracts';
export type { WorkExperienceFeedbackActionedProps };

export function trackWorkExperienceFeedbackActioned(
  props: WorkExperienceFeedbackActionedProps
): void {
  trackWorkEvent(WorkLaunchEventName.WorkExperienceFeedbackActioned, props);
}

// ─────────────────────────────────────────────────────────────────────────────
// Upgrade / paywall — single event, action discriminator
// Schema: iglu:com.heidi/work_upgrade_actioned/jsonschema/1-0-0
// ─────────────────────────────────────────────────────────────────────────────

export function trackWorkUpgradeActioned(props: WorkUpgradeActionedProps): void {
  trackWorkEvent(WorkLaunchEventName.WorkUpgradeActioned, props);
}

/**
 * Account SKU and this user's seat from the already-loaded billing cache.
 * Omits fields when the cache is cold. Never starts a fetch.
 */
export function resolveWorkBillingAnalyticsProps(): {
  current_plan?: string;
  current_seat_type?: WorkSeatType;
} {
  if (typeof getCurrentUserBillingInfo !== 'function') return {};
  let billing: ReturnType<typeof getCurrentUserBillingInfo>;
  try {
    billing = getCurrentUserBillingInfo();
  } catch {
    return {};
  }
  if (!billing) return {};

  const plan = billing.new_product_tier ?? billing.tier;
  const seat = mapBillingSeatType(billing.seat_type);

  return {
    ...(plan != null ? { current_plan: String(plan) } : {}),
    ...(seat != null ? { current_seat_type: seat } : {}),
  };
}

function mapBillingSeatType(
  seatType: BillingSeatType | string | null | undefined
): WorkSeatType | undefined {
  if (seatType == null) return undefined;
  const normalised = String(seatType).toLowerCase();
  if (normalised === 'max') return 'max';
  if (normalised === 'standard') return 'standard';
  if (normalised === 'assistant') return 'assistant';
  return undefined;
}

export function trackWorkArtifactCreated(props: {
  project_id?: string;
  agent_run_id?: string;
  artifact_type?: string;
  outcome?: 'succeeded' | 'failed';
}): void {
  trackWorkEvent(WorkLaunchEventName.WorkArtifactActioned, {
    action: 'created',
    object_type: 'artifact',
    surface: 'project_chat',
    ...resolveWorkBillingAnalyticsProps(),
    ...props,
  });
}

export function trackWorkArtifactExported(props: {
  project_id?: string;
  agent_run_id?: string;
  artifact_type?: string;
  outcome?: 'succeeded' | 'failed';
}): void {
  trackWorkEvent(WorkLaunchEventName.WorkArtifactActioned, {
    action: 'exported',
    object_type: 'artifact',
    surface: 'project_chat',
    ...resolveWorkBillingAnalyticsProps(),
    ...props,
  });
}
